/**
 * Homepage copy, closing CTA, and contact form editor.
 *
 * Every field here is optional. Blank means "use the copy shipped from the
 * deck", so clearing a field restores the original rather than emptying the
 * site — which is what someone experimenting actually wants.
 */
import { useEffect, useState } from 'react';
import { BUDGET_BANDS } from '@agenz/shared/content';
import { api } from '../lib/api.js';
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  PageTitle,
  SaveState,
  Spinner,
  Textarea,
} from '../components/ui.jsx';

const HERO_FIELDS = [
  { key: 'line1', label: 'Headline line 1', placeholder: 'Built to make' },
  { key: 'line2', label: 'Headline line 2', placeholder: 'brands' },
  { key: 'emphasis', label: 'Underlined word', placeholder: 'move.' },
  { key: 'primaryLabel', label: 'Primary button', placeholder: 'View the work' },
  { key: 'primaryHref', label: 'Primary link', placeholder: '/work' },
  { key: 'secondaryLabel', label: 'Secondary button', placeholder: 'Start a project' },
  { key: 'secondaryHref', label: 'Secondary link', placeholder: '/contact' },
];

const CTA_FIELDS = [
  { key: 'headline', label: 'Headline', placeholder: "Let's build something people actually stop scrolling for." },
  { key: 'buttonLabel', label: 'Button label', placeholder: "Let's build it" },
  { key: 'buttonHref', label: 'Button link', placeholder: '/contact' },
];

const FORM_FIELDS = [
  { key: 'heading', label: 'Form heading', placeholder: 'Tell us about the brand' },
  { key: 'messageLabel', label: 'Message field label', placeholder: 'What are you trying to do?' },
  { key: 'submitLabel', label: 'Submit button', placeholder: 'Send it' },
  { key: 'successTitle', label: 'Success heading', placeholder: 'Message sent' },
];

function BudgetBands({ bands, onChange }) {
  const rows = bands.length ? bands : BUDGET_BANDS;

  return (
    <div className="space-y-3">
      {rows.map((band, index) => (
        <div key={index} className="flex gap-3">
          <Input
            value={band}
            onChange={(e) => onChange(rows.map((b, i) => (i === index ? e.target.value : b)))}
          />
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          >
            ×
          </Button>
        </div>
      ))}
      <Button type="button" variant="secondary" size="sm" onClick={() => onChange([...rows, ''])}>
        + Add budget range
      </Button>
    </div>
  );
}

export default function Copy() {
  const [hero, setHero] = useState(null);
  const [cta, setCta] = useState(null);
  const [form, setForm] = useState(null);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);

  function load() {
    setError(null);
    api
      .get('/api/admin/settings')
      .then(({ settings }) => {
        setHero(settings?.heroCopy ?? {});
        setCta(settings?.cta ?? {});
        setForm(settings?.contactForm ?? {});
      })
      .catch((err) => setError(err.message));
  }

  useEffect(load, []);

  async function save() {
    setState('saving');
    setError(null);

    // Strip blanks so an empty field falls back to the shipped copy rather than
    // publishing an empty string.
    const clean = (obj) =>
      Object.fromEntries(
        Object.entries(obj).filter(([, v]) =>
          Array.isArray(v) ? v.filter(Boolean).length > 0 : String(v ?? '').trim() !== '',
        ),
      );

    try {
      await api.patch('/api/admin/settings', {
        heroCopy: clean(hero),
        cta: clean(cta),
        contactForm: clean({
          ...form,
          budgetBands: (form.budgetBands ?? []).filter(Boolean),
        }),
      });
      setState('saved');
      setTimeout(() => setState('idle'), 2500);
    } catch (err) {
      setState('error');
      setError(err.message);
    }
  }

  if (error && !hero) return <ErrorNote error={error} onRetry={load} />;
  if (!hero) return <Spinner />;

  return (
    <>
      <PageTitle
        title="Copy"
        description="Homepage headline, the closing call to action, and the contact form. Leave a field blank to keep the original wording. Changes reach the live site within a couple of seconds."
        action={
          <div className="flex items-center gap-4">
            <SaveState state={state} />
            <Button onClick={save} disabled={state === 'saving'}>
              Save copy
            </Button>
          </div>
        }
      />

      <ErrorNote error={error} />

      <div className="grid gap-8 lg:grid-cols-2">
        <Card className="p-8">
          <h2 className="text-xl">Hero</h2>
          <p className="mt-2 text-sm text-muted">
            The headline is split across three fields so the underline lands on the right word.
          </p>
          <div className="mt-6 space-y-5">
            {HERO_FIELDS.map((f) => (
              <Field key={f.key} label={f.label} htmlFor={`hero-${f.key}`}>
                <Input
                  id={`hero-${f.key}`}
                  value={hero[f.key] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => setHero({ ...hero, [f.key]: e.target.value })}
                />
              </Field>
            ))}
            <Field label="Sub-headline" htmlFor="hero-sub">
              <Textarea
                id="hero-sub"
                rows={3}
                value={hero.sub ?? ''}
                placeholder="We shape ideas into meaningful strategy, and transform content into real impact."
                onChange={(e) => setHero({ ...hero, sub: e.target.value })}
              />
            </Field>
          </div>
        </Card>

        <div className="space-y-8">
          <Card className="p-8">
            <h2 className="text-xl">Closing call to action</h2>
            <div className="mt-6 space-y-5">
              {CTA_FIELDS.map((f) => (
                <Field key={f.key} label={f.label} htmlFor={`cta-${f.key}`}>
                  <Input
                    id={`cta-${f.key}`}
                    value={cta[f.key] ?? ''}
                    placeholder={f.placeholder}
                    onChange={(e) => setCta({ ...cta, [f.key]: e.target.value })}
                  />
                </Field>
              ))}
              <Field label="Supporting line" htmlFor="cta-sub" hint="Optional, shown under the headline.">
                <Textarea
                  id="cta-sub"
                  rows={2}
                  value={cta.sub ?? ''}
                  onChange={(e) => setCta({ ...cta, sub: e.target.value })}
                />
              </Field>
            </div>
          </Card>

          <Card className="p-8">
            <h2 className="text-xl">Contact form</h2>
            <div className="mt-6 space-y-5">
              {FORM_FIELDS.map((f) => (
                <Field key={f.key} label={f.label} htmlFor={`form-${f.key}`}>
                  <Input
                    id={`form-${f.key}`}
                    value={form[f.key] ?? ''}
                    placeholder={f.placeholder}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                </Field>
              ))}

              <Field label="Intro paragraph" htmlFor="form-intro">
                <Textarea
                  id="form-intro"
                  rows={2}
                  value={form.intro ?? ''}
                  placeholder="The more context you give us, the sharper our first response will be."
                  onChange={(e) => setForm({ ...form, intro: e.target.value })}
                />
              </Field>

              <Field label="Success message" htmlFor="form-successBody">
                <Textarea
                  id="form-successBody"
                  rows={2}
                  value={form.successBody ?? ''}
                  placeholder="Got it. We will come back to you within two working days."
                  onChange={(e) => setForm({ ...form, successBody: e.target.value })}
                />
              </Field>

              <Field label="Budget ranges" hint="Shown in the budget dropdown.">
                <BudgetBands
                  bands={form.budgetBands ?? []}
                  onChange={(next) => setForm({ ...form, budgetBands: next })}
                />
              </Field>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
